export enum PermissionsScope {
  ADMIN = "admin",
  POS = "pos"
}

type PermissionsModule = PermissionsAdminModule | PermissionsPosModule;

export interface Permission {
  actions: PermissionsAction[];
  id: number;
  module: PermissionsModule;
  scope: PermissionsScope;
}

export enum PermissionsPosModule {
  ACCOUNTS = "accounts",
  CONTRACTS = "contracts",
  CONTRACT_SHIPMENTS = "contract-shipments",
  DASHBOARDS = "dashboards",
  LINES = "lines",
  OPERATIONS = "operations",
  USERS = "users",
  DASHBOARD_TAGS = "dashboards-tags",
  TERMS = "terms",
  INVOICES = "invoices",
  CONTRACTS_CHANGE_OF_OWNERSHIP_RECTIFICATION = "contracts:change-of-ownership:rectification"
}

export enum PermissionsAdminModule {
  ACCOUNTS = "accounts",
  BUSINESS_UNITS = "business-units",
  BUSINESS_UNIT_CLASSIFICATION = "business-unit-classification",
  CARRIERS = "carriers",
  CONTRACTS_CREDIT_FEES = "contracts-credit-fees",
  CONTRACTS_CREDIT_FEES_GROUPS = "contracts-credit-fees-groups",
  CONTRACTS_MODELS = "contracts-models",
  CONTRACTS_MODELS_ATTACHMENTS = "contracts-models-attachments",
  SERVICE_MODEL_OPTIONS_GROUP = "service-model-options-group",
  INVENTORIES = "inventories",
  INVENTORY_MOVEMENTS = "inventory-movements",
  INVENTORIES_DISTRIBUTION_CENTERS = "inventories-distribution-centers",
  INVENTORIES_OCCURRENCES = "inventories-occurrences",
  LINES = "lines",
  LINES_OCCURRENCES = "lines-occurrences",
  OFFERS = "offers",
  OFFERS_BANNERS = "offers-banners",
  OFFERS_PRICES = "offers-prices",
  OFFERS_SALES_CONDITIONS = "offers-sales-conditions",
  OPERATIONS = "operations",
  ORGANIZATIONS = "organizations",
  SALES_TEAMS = "sales-teams",
  SERVICE_PROVIDERS = "service-providers",
  PLANS = "plans",
  PERMISSIONS = "permissions",
  PRICING_GROUPS = "pricing-groups",
  PRODUCTS = "products",
  PRODUCTS_BRANDS = "products-brands",
  PRODUCTS_CATEGORIES = "products-categories",
  ROLES = "roles",
  SERVICES = "services",
  SERVICES_CATEGORIES = "services-categories",
  SHIPPINGS = "shippings",
  SHIPPINGS_GROUPS = "shippings-groups",
  SHIPPINGS_TYPES = "shippings-types",
  USERS = "users",
  ELECTRONIC_SIGNATURES = "electronic-signatures",
  EMAIL_TEMPLATE_GROUP = "email-template-group",
  EMAIL_TEMPLATE = "email-template",
  EMAIL_TRIGGER = "email-trigger",
  ADDITIONAL = "additional",
  ADDITIONAL_CATEGORY = "additional-category"
}

export enum PermissionsAction {
  ALL = "*",
  APPROVE_COMMERCIALLY = "approve-commercially",
  APPROVE_CREDIT = "approve-credit",
  APPROVE_DOCUMENT = "approve-document",
  APPROVE_LOYALTY_CHECK = "approve-loyalty-check",
  APPROVE_REPLACEMENT = "approve-replacement",
  AUDIT = "audit",
  CANCEL = "cancel",
  CONFIRM_SETUP_PAID = "confirm-setup-paid",
  CONTRACT_DELIVERED = "contract-delivered",
  CONTRACT_OCCURRENCES = "contract-occurrences",
  COPY = "copy",
  CREATE = "create",
  CREATED_PAYMENT_REQUEST = "created-payment-request",
  DELETE = "delete",
  DELIVER = "deliver",
  DELIVER_PRODUCT = "deliver-product",
  DELIVER_SERVICE = "deliver-service",
  DONE = "done",
  INTEGRATE = "integrate",
  LIST = "list",
  LIST_COSTS = "list-costs",
  LIST_LEGAL = "list-legal",
  LIST_PHYSICAL = "list-physical",
  MASS_UPDATE = "mass-update",
  OVERWRITE_CONTRACT = "overwrite-contract",
  OVERWRITE_SALES_TEAM = "overwrite-sales-team",
  PATCH_NOTE = "patch-note",
  READ = "read",
  RECOVERY_PASSWORD = "recovery-password",
  REGISTER_CORRECTION = "register-correction",
  REJECT = "reject",
  RESEND_SIGNATURES = "resend-signatures",
  SEARCH = "search",
  SEND_DOCUMENT = "send-document",
  SYSTEM_ALERT = "system-alert",
  TERM_OCCURRENCES = "term-occurrences",
  TRANSFER = "transfer",
  UPDATE = "update",
  VIEW_ALL = "view-all",
  WELCOME = "welcome"
}

export const PermissionsActionTranslated: Record<PermissionsAction, string> = {
  [PermissionsAction.ALL]: "Todas",

  [PermissionsAction.APPROVE_COMMERCIALLY]: "Aprovar Comercialmente",
  [PermissionsAction.APPROVE_CREDIT]: "Aprovar Crédito",
  [PermissionsAction.APPROVE_DOCUMENT]: "Aprovar Documento",
  [PermissionsAction.APPROVE_LOYALTY_CHECK]: "Aprovar Consulta de Fidelidade",
  [PermissionsAction.APPROVE_REPLACEMENT]: "Aprovar Substituição",

  [PermissionsAction.AUDIT]: "Auditar",
  [PermissionsAction.CANCEL]: "Cancelar",
  [PermissionsAction.CONFIRM_SETUP_PAID]: "Confirmar Pagamento de Adesão",

  [PermissionsAction.CONTRACT_DELIVERED]: "Liberar benefícios para o cooperado",
  [PermissionsAction.CONTRACT_OCCURRENCES]: "Ocorrências de Contrato",

  [PermissionsAction.COPY]: "Copiar itens de contrato",
  [PermissionsAction.CREATE]: "Criar",
  [PermissionsAction.CREATED_PAYMENT_REQUEST]: "Criar Solicitação de Pagamento",
  [PermissionsAction.DELETE]: "Deletar",

  [PermissionsAction.DELIVER]: "Entregar",
  [PermissionsAction.DELIVER_PRODUCT]: "Entregar Benefícios combos",
  [PermissionsAction.DELIVER_SERVICE]: "Entregar Liberação de Benefícios",

  [PermissionsAction.DONE]: "Concluir",
  [PermissionsAction.INTEGRATE]: "Integralizar",

  [PermissionsAction.LIST]: "Listar",
  [PermissionsAction.LIST_COSTS]: "Listar custos",
  [PermissionsAction.LIST_LEGAL]: "Listar registros de cooperados jurídicos",
  [PermissionsAction.LIST_PHYSICAL]: "Listar registros de cooperados físicos",

  [PermissionsAction.OVERWRITE_CONTRACT]: "Sobrescrever contrato",
  [PermissionsAction.OVERWRITE_SALES_TEAM]: "Atualizar times Comerciais",

  [PermissionsAction.PATCH_NOTE]: "Emitir alerta de atualização do sistema",
  [PermissionsAction.READ]: "Visualizar",
  [PermissionsAction.RECOVERY_PASSWORD]: "Recuperar senha",
  [PermissionsAction.REGISTER_CORRECTION]: "Correção cadastral",
  [PermissionsAction.REJECT]: "Rejeitar",
  [PermissionsAction.RESEND_SIGNATURES]: "Reenviar Assinaturas",
  [PermissionsAction.SEARCH]: "Buscar",
  [PermissionsAction.SEND_DOCUMENT]: "Enviar Documento",

  [PermissionsAction.SYSTEM_ALERT]: "Emitir alerta de sistema",
  [PermissionsAction.TERM_OCCURRENCES]: "Visualizar ocorrências de Termo",
  [PermissionsAction.TRANSFER]: "Transferir",
  [PermissionsAction.UPDATE]: "Atualizar",
  [PermissionsAction.MASS_UPDATE]: "mass-update",
  [PermissionsAction.VIEW_ALL]: "Visualizar Todos",
  [PermissionsAction.WELCOME]: "Enviar mensagem de Bem-vindo"
};

export const scopeOptions = [
  { key: PermissionsScope.ADMIN, text: "Painel Administrativo" },
  { key: PermissionsScope.POS, text: "Ponto de escolha de benefícios" }
];

export type PermissionsConfig = {
  [scope in PermissionsScope]?: {
    [
      module in PermissionsPosModule | PermissionsAdminModule
    ]?: Array<PermissionsAction>;
  };
};
